<template>
  <PageLoader v-if="!state.icon_loader" :logoUrl="tempTransparentLogo" />
  <Toast />
  <!-- <StickyHelpButton /> -->
  <template v-if="!state.page_loader">
    <WhatsAppButton />
    <router-view />
  </template>
</template>

<script setup>
// import StickyHelpButton from "@/components/StickyHelpButton.vue";
import WhatsAppButton from "@/components/whatsapp-button.vue";
import PageLoader from "@/components/PageLoader.vue";
import { useHead } from "@vueuse/head";
import { useScriptTag } from "@vueuse/core";
import { useAppartmentStore } from "@/stores/Store";
import { computed, watch, ref, reactive, onBeforeMount } from "vue";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { useLanguageStore } from "@/stores/languageStore";
const { locale } = useI18n();
const appartmentStore = useAppartmentStore();
const languageStore = useLanguageStore();

const elementRef = ref(null);
const toast = useToast();

const state = reactive({
  page_loader: false,
  icon_loader: false,
  isArabic: computed(() => languageStore.isArabic),
});

// Function to handle element changes
const handleElementChange = (newLocale) => {
  if (elementRef.value) {
    elementRef.value.dir = newLocale === "ar" ? "rtl" : "ltr";
  }
  document.documentElement.dir = newLocale === "ar" ? "rtl" : "ltr";
  localStorage.setItem("lang", newLocale);
};

// Watch for locale changes
watch(
  locale,
  (newLocale) => {
    handleElementChange(newLocale);
  },
  { immediate: true }
);

// Watch for element reference changes
watch(elementRef, (newElement) => {
  if (newElement) {
    handleElementChange(locale.value);
  }
});

// Assuming appartmentStore.settings is populated
const pageTitle = computed(() =>
  state.isArabic
    ? appartmentStore.settings.website_name__ar_
    : appartmentStore.settings.website_name__en_
);
const faviconUrl = computed(() =>
  appartmentStore.imageFormat(appartmentStore?.settings?.website_logo)
);

const tempTransparentLogo = computed(() => {
  return appartmentStore
    .imageFormat(appartmentStore?.settings?.transparent_logo)
    .replaceAll(" ", "%20");
});

useHead({
  title: "🔄 Loading...",
});

const useGoogleAnalytics = async (GA_ID) => {
  if (!GA_ID) return;
  useScriptTag(
    `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
    () => {
      // after script is loaded
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", GA_ID);
    },
    { async: true }
  );
};

// Separate function to update head
const updateHead = () => {
  useHead({
    title: pageTitle.value,
    meta: [
      {
        property: "og:title",
        content: pageTitle.value,
      },
      {
        property: "og:image",
        content: faviconUrl.value,
      },
      {
        property: "og:url",
        content: window.location.href,
      },
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:site_name",
        content: pageTitle.value,
      },
    ],
    link: [
      {
        rel: "icon",
        type: "image/x-icon",
        href: faviconUrl.value,
      },
    ],
  });
};

// Update head when isArabic changes
watch(
  () => state.isArabic,
  () => {
    if (
      appartmentStore.settings &&
      Object.keys(appartmentStore.settings).length > 0
    ) {
      updateHead();
    }
  }
);

onBeforeMount(() => {
  state.page_loader = true;
  state.icon_loader = true;
  appartmentStore.getSettings().subscribe(
    async (response) => {
      appartmentStore.settings = response.data.message.data.settings;
      state.icon_loader = false;
      // Update head after settings are loaded
      updateHead();

      await useGoogleAnalytics(appartmentStore.settings.google_analytics_key);
      state.page_loader = false;
    },
    () => {
      state.page_loader = false;
      state.icon_loader = false;
      toast.add({
        severity: "error",
        summary: "settings error",
        detail: "Failed to get settings!",
        life: 3000,
      });
    }
  );
});
</script>

<style>
/* Accessibility improvements for contrast */
.text-muted-2 {
  color: #575757 !important; /* Even darker shade for better contrast */
}

/* Make gold elements have darker, more accessible colors */
.text-navbar-icon-gold,
.text-primary,
.svg-icon-primary {
  color: #a07b33 !important; /* Darker gold for better contrast */
}

/* Fix for links without distinctive names */
a[href^="/grid/AREA-"] {
  position: relative;
}

a[href^="/grid/AREA-"]::before {
  content: "View Property";
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Make sure lists have proper structure */
ul:empty::before {
  content: "";
  display: block;
  height: 0;
}

ul.d-flex:has(> :not(li)) {
  display: flex !important;
  gap: 8px;
  list-style: none;
  padding-left: 0;
}

/* Ensure sufficient text contrast */
.fw-bold {
  font-weight: 700 !important;
}

/* Fix heading hierarchy */
.listing-card-info-price {
  margin-top: 0;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: bold;
  color: #a07b33 !important;
}

/* Screen reader text utility class */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
